import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Routes, Route, useNavigate, useLocation, Link, Navigate } from 'react-router-dom';
import { Truck, ShieldCheck, Gem, Headphones, Phone, Mail } from 'lucide-react';
import Navbar from './components/Navbar';
import BottomNav from './components/BottomNav';
import SearchOverlay from './components/SearchOverlay';
import Spinner from './components/Spinner';
import HomePage from './pages/HomePage';
import ProductPage from './pages/ProductPage';
import CategoryPage from './pages/CategoryPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ProfileCompletionPage from './pages/ProfileCompletionPage';
import ContactPage from './pages/ContactPage';
import AgentPortalPage from './pages/AgentPortalPage';
import VendorDashboardPage from './pages/VendorDashboardPage';
import VendorApplicationPage from './pages/VendorApplicationPage';
import VendorPublishProductPage from './pages/VendorPublishProductPage';
import LiveRoomPage from './pages/LiveRoomPage';
import SupportRoomsPage from './pages/SupportRoomsPage';
import LiveRoomsDiscoveryPage from './pages/LiveRoomsDiscoveryPage';
import MeetingJoinPage from './pages/MeetingJoinPage';

// Dedicated Professional Seller Platform
import SellerLayout from './seller/SellerLayout';
import SellerDashboard from './seller/SellerDashboard';
import SellerAnalytics from './seller/SellerAnalytics';
import SellerProductsList from './seller/SellerProductsList';
import AddProductWizard from './seller/AddProductWizard';
import SellerVideos from './seller/SellerVideos';
import SellerOrders from './seller/SellerOrders';
import SellerStore from './seller/SellerStore';
import SellerPosts from './seller/SellerPosts';
import SellerLive from './seller/SellerLive';
import SellerMessages from './seller/SellerMessages';
import SellerNotifications from './seller/SellerNotifications';
import SellerSettings from './seller/SellerSettings';
import AdminSellerControlPage from './admin/AdminSellerControlPage';

// Stores, Following & Public Seller Profiles
import StoresPage from './pages/StoresPage';
import SellerPublicProfilePage from './pages/SellerPublicProfilePage';
import FollowingFeedPage from './pages/FollowingFeedPage';

const VideosPage = React.lazy(() => import('./pages/VideosPage'));
const OrdersPage = React.lazy(() => import('./pages/OrdersPage'));
import DynamicPage from './pages/DynamicPage';
import DownloadPage from './pages/DownloadPage';
import VipPage from './pages/VipPage';
import LivePortalPage from './pages/LivePortalPage';
import MagazinePage from './pages/MagazinePage';
import SourcingPage from './pages/SourcingPage';
import LuxvooShippingHome from './pages/LuxvooShippingHome';
import StatusPage from './pages/StatusPage';
import ChatWidget from './components/ChatWidget';
import RealtimeInAppBanner from './components/RealtimeInAppBanner';
import { SOCIAL_PLATFORMS, getSocialIcon } from './components/SocialIcons';
import './chat.css';
import { useMarket } from './context/MarketContext';
import { MarketLink } from './components/MarketLink';
import GlobalMarketSelector from './components/GlobalMarketSelector';
import MarketLayout from './components/MarketLayout';
import LegacyRouteRedirect from './components/LegacyRouteRedirect';
import {
    getProducts,
    getCart,
    addToCart as apiAddToCart,
    removeFromCart as apiRemoveFromCart,
    removeFromCartItems as apiRemoveFromCartItems,
    createOrder as apiCreateOrder,
    confirmPayment as apiConfirmPayment
} from './api/client';
import SeoEngine from './components/SeoEngine';
import { isRTL, getTranslation as t } from './utils/translations';
import { detectAndApplyRegionalSettings, startPeriodicCountrySync, isSupportedLanguage, getBestSupportedLanguageForCountry } from './utils/countryLanguageEngine';
import { usePlugins } from './context/PluginContext';
import {
    DEFAULT_STOREFRONT_SETTINGS,
    normalizeStorefrontSettings
} from './utils/storefrontSettings';

const CART_STORAGE_KEY = 'luxvono_storefront_cart';

const RootMarketRedirect = () => {
    const target = useMemo(() => {
        if (typeof window !== 'undefined') {
            try {
                const raw = window.localStorage.getItem('luxvono_geo_context');
                if (raw) {
                    const parsed = JSON.parse(raw);
                    if (parsed && parsed.country && parsed.language) {
                        const c = String(parsed.country).toLowerCase();
                        let l = String(parsed.language).toLowerCase();
                        if (!isSupportedLanguage(l)) {
                            l = getBestSupportedLanguageForCountry(c);
                        }
                        if (/^[a-z]{2}$/.test(c)) {
                            return `/${c}/${l}/`;
                        }
                    }
                }
            } catch (_) {}
        }
        return '/sa/ar/';
    }, []);

    return <Navigate to={target} replace />;
};

const readStoredCart = () => {
    if (typeof window === 'undefined') return [];
    try {
        const raw = window.localStorage.getItem(CART_STORAGE_KEY);
        const parsed = JSON.parse(raw || '[]');
        return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
        return [];
    }
};

const normalizeProductId = (value) => {
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

const resolveCartMediaFromProduct = (product) => {
    if (!product || typeof product !== 'object') return '';
    const videoUrl = String(product.videoUrl || product.mediaUrl || '').trim();
    if (videoUrl) {
        const explicitVideoThumb = product.videoThumbnailUrl || product.videoThumbnail || product.videoPoster || product.poster;
        if (explicitVideoThumb) return explicitVideoThumb;
        const match = videoUrl.match(/^\/media\/videos\/([^/]+)\/master\.m3u8$/);
        if (match) return `/media/products/${match[1]}_thumb.jpg`;
    }

    const mainImage = product.primaryImageUrl || product.imageUrl || product.image || product.productImage;
    if (mainImage) return mainImage;

    if (Array.isArray(product.images) && product.images.length > 0) {
        const firstImage = product.images.find(Boolean);
        if (firstImage) return firstImage;
    }

    return product.thumbnailUrl || product.thumbnail || '';
};

const isColorDark = (hex) => {
    if (!hex) return false;
    const color = String(hex).trim().replace('#', '');
    if (color.length === 3) {
        const r = parseInt(color[0] + color[0], 16);
        const g = parseInt(color[1] + color[1], 16);
        const b = parseInt(color[2] + color[2], 16);
        return (r * 0.299 + g * 0.587 + b * 0.114) < 120;
    }
    if (color.length === 6) {
        const r = parseInt(color.substring(0, 2), 16);
        const g = parseInt(color.substring(2, 4), 16);
        const b = parseInt(color.substring(4, 6), 16);
        return (r * 0.299 + g * 0.587 + b * 0.114) < 120;
    }
    if (String(hex).toLowerCase().startsWith('rgb')) {
        const match = String(hex).match(/\d+/g);
        if (match && match.length >= 3) {
            const r = parseInt(match[0], 10);
            const g = parseInt(match[1], 10);
            const b = parseInt(match[2], 10);
            return (r * 0.299 + g * 0.587 + b * 0.114) < 120;
        }
    }
    return false;
};

const App = () => {
    const [cart, setCart] = useState(() => readStoredCart());
    const [searchOpen, setSearchOpen] = useState(false);
    const [initialSearchMode, setInitialSearchMode] = useState('text');
    const [initialSearchImage, setInitialSearchImage] = useState(null);
    const [activeLiveRoomId, setActiveLiveRoomId] = useState(null);

    // Forces re-render when language changes
    const [langVersion, setLangVersion] = useState(0);

    const [allProducts, setAllProducts] = useState([]);
    const cartSyncRequestRef = useRef(0);
    const { activePlugins, isPluginActive, getPluginConfig, pluginsLoaded } = usePlugins();
    const { getMarketPath, getMarketProductPath } = useMarket();
    const navigate = useNavigate();
    const routeLocation = useLocation();

    const [userTheme, setUserTheme] = useState(() => {
        if (typeof window === 'undefined') return 'light';
        return window.localStorage.getItem('luxvono_theme') || 'light';
    });
    const [systemPrefersDark, setSystemPrefersDark] = useState(false);

    useEffect(() => {
        // Load cart on mount
        let cancelled = false;
        getCart().then((remoteCart) => {
            if (cancelled) return;
            const safeRemote = Array.isArray(remoteCart) ? remoteCart : [];
            setCart(safeRemote);
        }).catch(e => {
            console.warn("Failed to sync cart, retaining local fallback state:", e.message);
        });

        const handleCartRefresh = () => {
            getCart().then((remoteCart) => {
                if (cancelled) return;
                const safeRemote = Array.isArray(remoteCart) ? remoteCart : [];
                setCart(safeRemote);
            }).catch(e => {
                console.warn("Cart refresh failed:", e.message);
            });
        };

        if (typeof window !== 'undefined') {
            window.addEventListener('luxvono_cart_refresh', handleCartRefresh);
        }

        const handleJoinRoom = (e) => setActiveLiveRoomId(e.detail);
        const handleLangChange = () => setLangVersion(v => v + 1);

        if (typeof window !== 'undefined') {
            window.addEventListener('luxvono_join_live_room', handleJoinRoom);
            window.addEventListener('languageChange', handleLangChange);
            window.addEventListener('geoContextChange', handleLangChange);
        }

        // Initialize and start periodic 1-minute live country, language, and currency sync
        const stopSync = startPeriodicCountrySync(60000);

        return () => {
            cancelled = true;
            if (stopSync) stopSync();
            if (typeof window !== 'undefined') {
                window.removeEventListener('luxvono_cart_refresh', handleCartRefresh);
                window.removeEventListener('luxvono_join_live_room', handleJoinRoom);
                window.removeEventListener('languageChange', handleLangChange);
                window.removeEventListener('geoContextChange', handleLangChange);
            }
        };
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        window.localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(Array.isArray(cart) ? cart : []));
    }, [cart]);

    const routeNavigationCountRef = useRef(0);
    const lastBackPressTimeRef = useRef(0);

    useEffect(() => {
        routeNavigationCountRef.current += 1;
    }, [routeLocation.pathname]);

    // Mobile Back Navigation & Root History Guard Engine
    useEffect(() => {
        if (typeof window === 'undefined') return;

        const basePath = getMarketPath('') || '/';
        const isCurrentHome = (path) => {
            const clean = String(path || '').replace(/\/+$/, '');
            const cleanBase = String(basePath).replace(/\/+$/, '');
            return clean === '' || clean === cleanBase || clean === '/sa/ar' || clean === '/sa/en' || clean === '/us/en';
        };

        const hasSessionRoot = window.sessionStorage.getItem('luxvono_has_root_history');
        if (!hasSessionRoot) {
            window.sessionStorage.setItem('luxvono_has_root_history', 'true');
            if (!isCurrentHome(routeLocation.pathname)) {
                window.history.replaceState({ luxvonoBase: true, path: basePath }, '', basePath);
                window.history.pushState({ luxvonoCurrent: true, path: routeLocation.pathname }, '', routeLocation.pathname);
            }
        }

        const handleGlobalPopState = (e) => {
            // If popstate is due to a modal closing (e.g. search, chat, sidebar), let the modal handler manage it
            if (e.state?.luxvonoModal || e.state?.luxvonoChatLevel || window.__luxvono_chat_level || window.__luxvono_handling_chat_back) {
                return;
            }

            const onHome = isCurrentHome(routeLocation.pathname);

            // If user hit the base history state or reached beginning of history while on a subpage:
            if (e.state?.luxvonoBase || (!onHome && routeNavigationCountRef.current <= 1)) {
                // Instantly navigate to home screen instead of exiting the website
                navigate(basePath, { replace: true });
                return;
            }

            // If user is already on the Home screen and presses back:
            if (onHome) {
                const now = Date.now();
                if (now - lastBackPressTimeRef.current < 2000) {
                    // Two rapid back presses: allow default browser exit behavior
                    return;
                }
                lastBackPressTimeRef.current = now;
                // Push home guard so they stay on site on first press
                window.history.pushState({ luxvonoHomeGuard: true }, '', basePath);

                // Show discreet mobile toast
                let toast = document.getElementById('lux-back-toast');
                if (!toast) {
                    toast = document.createElement('div');
                    toast.id = 'lux-back-toast';
                    toast.style.cssText = 'position:fixed;bottom:75px;left:50%;transform:translateX(-50%);background:rgba(15,23,42,0.92);color:#ffffff;padding:8px 18px;border-radius:30px;font-size:12.5px;font-weight:600;z-index:999999;backdrop-filter:blur(10px);box-shadow:0 6px 20px rgba(0,0,0,0.3);pointer-events:none;transition:opacity 0.25s ease;opacity:0;';
                    document.body.appendChild(toast);
                }
                toast.textContent = isRTL() ? 'اضغط رجوع مرة أخرى للخروج من الموقع' : 'Press back again to exit';
                toast.style.opacity = '1';
                clearTimeout(toast._timer);
                toast._timer = setTimeout(() => {
                    if (toast) toast.style.opacity = '0';
                }, 2000);
            }
        };

        window.addEventListener('popstate', handleGlobalPopState);
        return () => window.removeEventListener('popstate', handleGlobalPopState);
    }, [routeLocation.pathname, getMarketPath, navigate]);

    // Search overlay back button listener
    useEffect(() => {
        if (!searchOpen) return;
        if (typeof window !== 'undefined') {
            window.history.pushState({ luxvonoModal: 'search' }, '');
        }
        const handlePopState = () => {
            setSearchOpen(false);
        };
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [searchOpen]);

    const handleCloseSearch = (skipHistoryBack = false) => {
        setSearchOpen(false);
        if (!skipHistoryBack && typeof window !== 'undefined' && window.history.state?.luxvonoModal === 'search') {
            window.history.back();
        }
    };

    // Advanced SEO Plugin logic
    useEffect(() => {
        if (pluginsLoaded && isPluginActive('advanced_seo')) {
            const config = getPluginConfig('advanced_seo');
            if (config.enableJsonLd) {
                let script = document.getElementById('seo-json-ld');
                if (!script) {
                    script = document.createElement('script');
                    script.id = 'seo-json-ld';
                    script.type = 'application/ld+json';
                    document.head.appendChild(script);
                }
                script.textContent = JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "WebSite",
                    "url": window.location.href,
                    "name": "LuxVono"
                });
            }
        } else if (pluginsLoaded && !isPluginActive('advanced_seo')) {
            const script = document.getElementById('seo-json-ld');
            if (script) document.head.removeChild(script);
        }
    }, [getPluginConfig, isPluginActive, pluginsLoaded]);

    const addToCart = async (product) => {
        const productId = normalizeProductId(product?.id);
        if (!productId) return;

        const optimisticImage = resolveCartMediaFromProduct(product);
        const optimisticName = String(product?.name || 'Product');
        const optimisticCategory = String(product?.categoryName || product?.category || '');
        const optimisticPrice = Number(product?.price) || 0;

        // Optimistic update
        setCart(prev => {
            const existing = prev.findIndex(c => String(c.id) === String(productId));
            if (existing >= 0) {
                const updated = [...prev];
                const currentQty = Number(updated[existing]?.qty) || 0;
                updated[existing] = {
                    ...updated[existing],
                    id: productId,
                    qty: currentQty + 1,
                    image: updated[existing]?.image || optimisticImage,
                    name: updated[existing]?.name || optimisticName,
                    category: updated[existing]?.category || optimisticCategory,
                    price: Number(updated[existing]?.price) || optimisticPrice
                };
                return updated;
            }
            return [...prev, {
                ...product,
                id: productId,
                name: optimisticName,
                category: optimisticCategory,
                price: optimisticPrice,
                image: optimisticImage,
                qty: 1
            }];
        });

        // Sync with backend
        const requestId = ++cartSyncRequestRef.current;
        try {
            const newCart = await apiAddToCart({ ...product, id: productId }, 1);
            if (requestId === cartSyncRequestRef.current) {
                setCart(Array.isArray(newCart) ? newCart : []);
            }
        } catch (e) {
            console.error("Failed to sync cart", e);
        }
    };

    const updateQty = (index, delta) => {
        // Need cartItemId to update properly, but for now just optimistic local
        // Real implementation needs backend update endpoint
        setCart(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], qty: Math.max(1, updated[index].qty + delta) };
            return updated;
        });
    };

    const removeItem = async (index) => {
        const item = cart[index];
        setCart(prev => prev.filter((_, i) => i !== index));
        const itemIds = Array.isArray(item?.cartItemIds) && item.cartItemIds.length > 0
            ? item.cartItemIds
            : (item?.cartItemId ? [item.cartItemId] : []);

        if (itemIds.length > 0) {
            try {
                const newCart = itemIds.length > 1
                    ? await apiRemoveFromCartItems(itemIds)
                    : await apiRemoveFromCart(itemIds[0]);
                setCart(newCart);
            } catch (e) {
                console.error("Failed to remove item", e);
            }
        }
    };

    const checkout = async () => {
        if (cart.length === 0) {
            throw new Error('Cart is empty');
        }

        const order = await apiCreateOrder(cart, 'CARD');

        if (!order?.paymentId) {
            throw new Error('Payment was not initialized');
        }

        const providerPaymentId = order.clientSecret || `frontend_${Date.now()}`;
        await apiConfirmPayment(order.paymentId, providerPaymentId);

        const freshCart = await getCart();
        setCart(freshCart);

        return order;
    };

    const buyNow = async (product) => {
        if (!product?.id) {
            throw new Error('Invalid product for Buy Now');
        }

        const quickOrderItems = [{ ...product, qty: 1 }];
        const order = await apiCreateOrder(quickOrderItems, 'CARD');

        if (!order?.paymentId) {
            throw new Error('Payment was not initialized');
        }

        const providerPaymentId = order.clientSecret || `frontend_${Date.now()}`;
        await apiConfirmPayment(order.paymentId, providerPaymentId);
        navigate(getMarketProductPath(product));
        return order;
    };

    const handleSearchOpen = (mode = 'text', imageUrl = null) => {
        if (allProducts.length === 0) {
            getProducts().then(res => setAllProducts(res.data || []));
        }
        setInitialSearchMode(mode);
        setInitialSearchImage(imageUrl);
        setSearchOpen(true);
    };

    const cartCount = cart.reduce((sum, item) => sum + item.qty, 0);
    const [draftSettings, setDraftSettings] = useState(null);

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        const handleMessage = (event) => {
            try {
                if (event.data?.type === 'STOREFRONT_PREVIEW_DRAFT_SETTINGS') {
                    setDraftSettings(event.data.settings);
                }
            } catch (_) {}
        };
        window.addEventListener('message', handleMessage);
        try {
            if (window.parent && window.parent !== window) {
                window.parent.postMessage({ type: 'STOREFRONT_PREVIEW_READY' }, '*');
            }
        } catch (_) {}
        return () => window.removeEventListener('message', handleMessage);
    }, []);

    const storefrontSettings = useMemo(() => {
        if (draftSettings) {
            return normalizeStorefrontSettings(draftSettings);
        }
        if (!pluginsLoaded || !activePlugins) {
            return DEFAULT_STOREFRONT_SETTINGS;
        }
        if (!isPluginActive('advanced_seo')) {
            return DEFAULT_STOREFRONT_SETTINGS;
        }
        const pluginConfig = getPluginConfig('advanced_seo') || {};
        return normalizeStorefrontSettings(pluginConfig.storefrontSettings || {});
    }, [draftSettings, activePlugins, getPluginConfig, isPluginActive, pluginsLoaded]);

    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return undefined;
        const media = window.matchMedia('(prefers-color-scheme: dark)');
        const onChange = (event) => setSystemPrefersDark(event.matches);
        media.addEventListener?.('change', onChange);
        return () => media.removeEventListener?.('change', onChange);
    }, []);

    // Extract market-agnostic subpath: /us/en/product/1 -> /product/1
    const currentSubpath = useMemo(() => {
        const p = routeLocation.pathname.replace(/^\/[a-zA-Z]{2}\/[a-zA-Z]{2,3}(?:-[a-zA-Z]{2})?/, '');
        return p ? (p.startsWith('/') ? p : `/${p}`) : '/';
    }, [routeLocation.pathname]);

    const isRootSelector = routeLocation.pathname === '/select-market' || routeLocation.pathname === '/market-selector';
    const isAuthPage = ['/login', '/register', '/forgot-password', '/complete-profile'].includes(currentSubpath);
    const isAgentPortal = currentSubpath.startsWith('/agent-portal');
    const isVideosPage = currentSubpath.startsWith('/videos');
    const isLiveRoom = currentSubpath.startsWith('/live');
    const isMeeting = currentSubpath.startsWith('/meeting');
    const isProductPage = currentSubpath.startsWith('/product/') || currentSubpath.startsWith('/products/');
    const isSeller = currentSubpath.startsWith('/seller') || routeLocation.pathname.includes('/seller');

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const stored = window.localStorage.getItem('luxvono_theme');
        if (!stored && storefrontSettings.darkModeDefault && storefrontSettings.darkModeDefault !== 'auto') {
            setUserTheme(storefrontSettings.darkModeDefault);
        }
    }, [storefrontSettings.darkModeDefault]);

    const isDarkMode = useMemo(() => {
        if (userTheme === 'dark') return true;
        if (userTheme === 'light') return false;
        if (storefrontSettings.darkModeDefault === 'dark') return true;
        if (storefrontSettings.darkModeDefault === 'light') return false;
        if (isColorDark(storefrontSettings.backgroundColor)) return true;
        return false;
    }, [userTheme, storefrontSettings.darkModeDefault, storefrontSettings.backgroundColor]);

    useEffect(() => {
        if (typeof document === 'undefined') return;
        if (isDarkMode) {
            document.documentElement.classList.add('theme-dark', 'dark-mode');
            document.documentElement.classList.remove('theme-light', 'light-mode');
            document.body.classList.add('theme-dark', 'dark-mode');
            document.body.classList.remove('theme-light', 'light-mode');
        } else {
            document.documentElement.classList.add('theme-light', 'light-mode');
            document.documentElement.classList.remove('theme-dark', 'dark-mode');
            document.body.classList.add('theme-light', 'light-mode');
            document.body.classList.remove('theme-dark', 'dark-mode');
        }
    }, [isDarkMode]);

    const toggleTheme = () => {
        const next = isDarkMode ? 'light' : 'dark';
        setUserTheme(next);
        if (typeof window !== 'undefined') {
            window.localStorage.setItem('luxvono_theme', next);
        }
    };

    const rootStyle = useMemo(() => {
        const theme = isDarkMode ? {
            primary: '#ffffff',
            accent: '#FE2C55',
            bg: '#0D0E15',
            card: '#151722',
            text: '#ffffff',
            textMuted: '#94A3B8',
            border: 'rgba(255, 255, 255, 0.12)',
            navBg: '#0D0E15',
            navText: '#ffffff',
            cardBg: '#151722',
            cardBorder: 'rgba(255, 255, 255, 0.12)'
        } : {
            primary: storefrontSettings.primaryColor || '#111111',
            accent: storefrontSettings.accentColor || '#FE2C55',
            bg: storefrontSettings.backgroundColor || '#FAFAF9',
            card: storefrontSettings.cardColor || '#ffffff',
            text: storefrontSettings.textColor || '#111111',
            textMuted: storefrontSettings.textMutedColor || '#64748b',
            border: storefrontSettings.borderColor || '#e5e7eb',
            navBg: storefrontSettings.navBg || '#ffffff',
            navText: storefrontSettings.navText || '#111111',
            cardBg: storefrontSettings.cardBg || '#ffffff',
            cardBorder: storefrontSettings.cardBorder || 'rgba(0, 0, 0, 0.06)'
        };

        return {
            '--lux-primary': theme.primary,
            '--lux-accent': theme.accent,
            '--lux-bg': theme.bg,
            '--bg-card': theme.card,
            '--text': theme.text,
            '--text-muted': theme.textMuted,
            '--border': theme.border,
            '--lux-glass-opacity': storefrontSettings.glassOpacity || 0.85,
            '--lux-btn-bg': theme.primary,
            '--lux-btn-text': isDarkMode ? '#030816' : '#ffffff',
            '--lux-nav-bg': theme.navBg,
            '--lux-nav-text': theme.navText,
            '--lux-footer-bg': isDarkMode ? '#02050e' : '#090a0f',
            '--lux-footer-text': '#e5e7eb',
            '--lux-card-bg': theme.cardBg,
            '--lux-card-border': theme.cardBorder,
            '--lux-shadow': isDarkMode ? '0 12px 40px 0 rgba(0, 0, 0, 0.45)' : (storefrontSettings.customShadow || '0 4px 20px rgba(0, 0, 0, 0.04)'),
            '--lux-line': theme.border,
            '--lux-font-family': storefrontSettings.fontFamily || 'Inter, Cairo, sans-serif',
            '--lux-radius-lg': storefrontSettings.radiusLg || '24px',
            '--lux-radius-md': storefrontSettings.radiusMd || '16px',
            '--lux-spacing-unit': storefrontSettings.spacingUnit || '24px',
            '--lux-transition-speed': storefrontSettings.transitionSpeed || '0.3s'
        };
    }, [isDarkMode, storefrontSettings]);

    const publicPages = useMemo(() => {
        const pages = Array.isArray(storefrontSettings?.pages) ? storefrontSettings.pages : [];
        return pages.filter(page => page?.visible !== false && String(page?.visibility || '').toLowerCase() !== 'hidden');
    }, [storefrontSettings?.pages]);

    const footerPages = useMemo(() => {
        const preferred = ['privacy', 'terms', 'about'];
        const bySlug = new Map(publicPages.map(page => [String(page.slug || '').toLowerCase(), page]));
        const selected = [];
        preferred.forEach(slug => {
            const found = bySlug.get(slug);
            if (found) selected.push(found);
        });
        const extras = publicPages.filter(page => !selected.some(item => item.slug === page.slug)).slice(0, 3);
        return [...selected, ...extras].slice(0, 5);
    }, [publicPages]);

    const socialLinks = useMemo(() => {
        const social = storefrontSettings?.socialMedia || {};
        return SOCIAL_PLATFORMS.map(p => ({
            key: p.key,
            label: isRTL() ? p.labelAr : p.label,
            href: social[p.key],
            color: p.color,
            bgColor: p.bgColor,
            borderColor: p.borderColor,
            icon: p.icon
        })).filter(item => String(item.href || '').trim());
    }, [storefrontSettings?.socialMedia]);

    useEffect(() => {
        if (isDarkMode) {
            document.documentElement.classList.add('theme-dark', 'dark-mode');
            document.documentElement.classList.remove('theme-light');
        } else {
            document.documentElement.classList.add('theme-light');
            document.documentElement.classList.remove('theme-dark', 'dark-mode');
        }

        const darkThemeDefaults = {
            primaryColor: '#FE2C55',
            accentColor: '#FE2C55',
            backgroundColor: '#0D0E15',
            btnBg: '#FE2C55',
            btnText: '#ffffff',
            navBg: '#0D0E15',
            navText: '#F9FAFB',
            footerBg: '#090A0F',
            footerText: '#E5E7EB',
            cardBg: '#151722',
            cardBorder: 'rgba(255, 255, 255, 0.08)',
            customShadow: '0 12px 40px 0 rgba(0, 0, 0, 0.6)',
            customBorderColor: 'rgba(255, 255, 255, 0.08)'
        };

        const getThemeVal = (key, fallbackDefault) => {
            const val = storefrontSettings[key];
            const defaultLight = DEFAULT_STOREFRONT_SETTINGS[key];
            const isDefault = !val || val === defaultLight || val === '#ffffff' || val === '#111111';
            if (isDarkMode && (isDefault || key === 'backgroundColor' || key === 'navBg' || key === 'cardBg') && darkThemeDefaults[key] !== undefined) {
                return darkThemeDefaults[key];
            }
            return val || fallbackDefault;
        };

        const resolvedPrimary = getThemeVal('primaryColor', isDarkMode ? '#FE2C55' : '#111111');
        const resolvedAccent = getThemeVal('accentColor', isDarkMode ? '#FE2C55' : '#FE2C55');
        const resolvedBg = getThemeVal('backgroundColor', isDarkMode ? '#0D0E15' : '#FAFAF9');

        document.documentElement.style.setProperty('--lux-primary', resolvedPrimary);
        document.documentElement.style.setProperty('--lux-accent', resolvedAccent);
        document.documentElement.style.setProperty('--lux-bg', resolvedBg);

        const optionalColors = [
            { key: 'cardColor', cssVar: '--bg-card-custom' },
            { key: 'textColor', cssVar: '--text-custom' },
            { key: 'textMutedColor', cssVar: '--text-muted-custom' },
            { key: 'borderColor', cssVar: '--border-custom' }
        ];

        optionalColors.forEach(({ key, cssVar }) => {
            const val = getThemeVal(key, '');
            if (val && val.trim() !== '') {
                document.documentElement.style.setProperty(cssVar, val);
            } else {
                document.documentElement.style.removeProperty(cssVar);
            }
        });

        // Dynamic theme customization variables mapping
        const customThemeProps = [
            { key: 'glassOpacity', cssVar: '--lux-glass-opacity' },
            { key: 'btnBg', cssVar: '--lux-btn-bg' },
            { key: 'btnText', cssVar: '--lux-btn-text' },
            { key: 'navBg', cssVar: '--lux-nav-bg' },
            { key: 'navText', cssVar: '--lux-nav-text' },
            { key: 'footerBg', cssVar: '--lux-footer-bg' },
            { key: 'footerText', cssVar: '--lux-footer-text' },
            { key: 'cardBg', cssVar: '--lux-card-bg' },
            { key: 'cardBorder', cssVar: '--lux-card-border' },
            { key: 'customShadow', cssVar: '--lux-shadow' },
            { key: 'customBorderColor', cssVar: '--lux-line' },
            { key: 'fontFamily', cssVar: '--lux-font-family' },
            { key: 'radiusLg', cssVar: '--lux-radius-lg' },
            { key: 'radiusMd', cssVar: '--lux-radius-md' },
            { key: 'spacingUnit', cssVar: '--lux-spacing-unit' },
            { key: 'transitionSpeed', cssVar: '--lux-transition-speed' }
        ];

        customThemeProps.forEach(({ key, cssVar }) => {
            const val = getThemeVal(key, DEFAULT_STOREFRONT_SETTINGS[key]);
            if (val !== undefined && val !== null && String(val).trim() !== '') {
                document.documentElement.style.setProperty(cssVar, String(val));
            } else {
                document.documentElement.style.removeProperty(cssVar);
            }
        });
    }, [isDarkMode, storefrontSettings]);

    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    if (hostname === 'app.luxvono.com') return <DownloadPage />;
    if (hostname === 'vip.luxvono.com') return <VipPage />;
    if (hostname === 'live.luxvono.com') return <LivePortalPage onAddToCart={addToCart} onBuyNow={buyNow} cartCount={cartCount} />;
    if (hostname === 'magazine.luxvono.com') return <MagazinePage />;
    if (hostname === 'sourcing.luxvono.com') return <SourcingPage />;

    return (
        <div
            key={`lang-${langVersion}`} // Force full re-render on language change for immediate translations
            className={`luxvono-root ${isRTL() ? 'rtl-layout' : ''} ${isDarkMode ? 'theme-dark dark-mode' : 'theme-light'} ${isSeller ? 'is-seller-portal' : ''}`}
            dir={isRTL() ? 'rtl' : 'ltr'}
            style={{ ...rootStyle, ...(isAuthPage ? { backgroundColor: '#000000' } : {}), ...(isLiveRoom || isMeeting ? { backgroundColor: '#090A0F' } : {}), ...(isSeller ? { backgroundColor: isDarkMode ? '#0a0a0a' : '#f8f9fa' } : {}) }}
            data-button-style={storefrontSettings.buttonStyle || 'solid'}
            data-theme-mode={userTheme}
        >
            <SeoEngine />
            {(!isRootSelector && !isAuthPage && !isAgentPortal && !isVideosPage && !isLiveRoom && !isMeeting && !isSeller) && (
                <Navbar
                    cartCount={cartCount}
                    onSearchClick={handleSearchOpen}
                    storeSettings={storefrontSettings}
                    isDarkMode={isDarkMode}
                    toggleTheme={toggleTheme}
                    themeMode={userTheme}
                />
            )}

            <main className={`main-content ${isAuthPage ? 'auth-main-content-reset' : ''} ${(isLiveRoom || isMeeting) ? 'live-main-content-reset' : ''} ${isSeller ? 'seller-main-content-reset' : ''}`}>
                <Routes>
                    {/* Dedicated Professional Seller / Vendor Web Application */}
                    <Route path="/seller" element={<SellerLayout />}>
                        <Route index element={<Navigate to="dashboard" replace />} />
                        <Route path="dashboard" element={<SellerDashboard />} />
                        <Route path="analytics" element={<SellerAnalytics />} />
                        <Route path="products" element={<SellerProductsList />} />
                        <Route path="products/new" element={<AddProductWizard />} />
                        <Route path="products/drafts" element={<SellerProductsList initialTab="DRAFTS" />} />
                        <Route path="products/pending" element={<SellerProductsList initialTab="PENDING" />} />
                        <Route path="products/published" element={<SellerProductsList initialTab="PUBLISHED" />} />
                        <Route path="products/rejected" element={<SellerProductsList initialTab="REJECTED" />} />
                        <Route path="posts" element={<SellerPosts />} />
                        <Route path="live" element={<SellerLive />} />
                        <Route path="messages" element={<SellerMessages />} />
                        <Route path="videos" element={<SellerVideos />} />
                        <Route path="orders" element={<SellerOrders />} />
                        <Route path="store" element={<SellerStore />} />
                        <Route path="notifications" element={<SellerNotifications />} />
                        <Route path="settings" element={<SellerSettings />} />
                        <Route path="admin-control" element={<AdminSellerControlPage />} />
                    </Route>
                    <Route path="/admin/sellers" element={<AdminSellerControlPage />} />

                    {/* Direct Canonical Public Store Routes (Phases 11, 19, 21) */}
                    <Route path="/@:username" element={<SellerPublicProfilePage />} />
                    <Route path="/store/:slug" element={<SellerPublicProfilePage />} />
                    <Route path="/seller/:sellerSlug" element={<SellerPublicProfilePage />} />

                    {/* 1. Instant Synchronous Market Routing (0ms, zero flash) */}
                    <Route path="/" element={<RootMarketRedirect />} />
                    <Route path="/select-market" element={<GlobalMarketSelector autoForward={false} />} />
                    <Route path="/market-selector" element={<GlobalMarketSelector autoForward={false} />} />

                    {/* 2. Global SHEIN-Style Market Routing: /{country}/{language}/... */}
                    <Route path="/:country/:language" element={<MarketLayout />}>
                        <Route
                            index
                            element={
                                <HomePage
                                    onAddToCart={addToCart}
                                    onBuyNow={buyNow}
                                    storeSettings={storefrontSettings}
                                />
                            }
                        />
                        <Route
                            path="products"
                            element={
                                <HomePage
                                    onAddToCart={addToCart}
                                    onBuyNow={buyNow}
                                    storeSettings={storefrontSettings}
                                />
                            }
                        />
                        <Route path="shipping" element={<LuxvooShippingHome />} />
                        <Route path="sourcing" element={<LuxvooShippingHome />} />
                        <Route
                            path="product/:id"
                            element={<ProductPage onAddToCart={addToCart} onBuyNow={buyNow} storeSettings={storefrontSettings} />}
                        />
                        <Route
                            path="products/:id"
                            element={<ProductPage onAddToCart={addToCart} onBuyNow={buyNow} storeSettings={storefrontSettings} />}
                        />
                        <Route path="videos" element={
                            <React.Suspense fallback={<div className="container" style={{ paddingTop: '100px', textAlign: 'center' }}><Spinner /></div>}>
                                <VideosPage onAddToCart={addToCart} onBuyNow={buyNow} storeSettings={storefrontSettings} cartCount={cartCount} />
                            </React.Suspense>
                        } />
                        <Route path="video/:id" element={
                            <React.Suspense fallback={<div className="container" style={{ paddingTop: '100px', textAlign: 'center' }}><Spinner /></div>}>
                                <VideosPage onAddToCart={addToCart} onBuyNow={buyNow} storeSettings={storefrontSettings} cartCount={cartCount} />
                            </React.Suspense>
                        } />
                        <Route
                            path="category/:name"
                            element={<CategoryPage onAddToCart={addToCart} storeSettings={storefrontSettings} />}
                        />
                        <Route
                            path="categories"
                            element={<CategoryPage onAddToCart={addToCart} storeSettings={storefrontSettings} />}
                        />
                        <Route path="cart" element={<CartPage cart={cart} onUpdateQty={updateQty} onRemove={removeItem} onCheckout={checkout} storeSettings={storefrontSettings} />} />
                        <Route
                            path="checkout"
                            element={<CheckoutPage cart={cart} onComplete={() => setCart([])} storeSettings={storefrontSettings} />}
                        />
                        <Route path="orders" element={
                            <React.Suspense fallback={<div className="container" style={{ paddingTop: '100px', textAlign: 'center' }}><Spinner /></div>}>
                                <OrdersPage />
                            </React.Suspense>
                        } />
                        <Route path="contact" element={<ContactPage storeSettings={storefrontSettings} />} />
                        <Route path="login" element={<LoginPage />} />
                        <Route path="register" element={<RegisterPage />} />
                        <Route path="forgot-password" element={<ForgotPasswordPage />} />
                        <Route path="complete-profile" element={<ProfileCompletionPage />} />
                        <Route path="account/profile" element={<ProfileCompletionPage />} />
                        
                        {/* Nested Market-Aware Seller Application Area */}
                        <Route path="seller" element={<SellerLayout />}>
                            <Route index element={<Navigate to="dashboard" replace />} />
                            <Route path="dashboard" element={<SellerDashboard />} />
                            <Route path="analytics" element={<SellerAnalytics />} />
                            <Route path="products" element={<SellerProductsList />} />
                            <Route path="products/new" element={<AddProductWizard />} />
                            <Route path="products/drafts" element={<SellerProductsList initialTab="DRAFTS" />} />
                            <Route path="products/pending" element={<SellerProductsList initialTab="PENDING" />} />
                            <Route path="products/published" element={<SellerProductsList initialTab="PUBLISHED" />} />
                            <Route path="products/rejected" element={<SellerProductsList initialTab="REJECTED" />} />
                            <Route path="posts" element={<SellerPosts />} />
                            <Route path="live" element={<SellerLive />} />
                            <Route path="messages" element={<SellerMessages />} />
                            <Route path="videos" element={<SellerVideos />} />
                            <Route path="orders" element={<SellerOrders />} />
                            <Route path="store" element={<SellerStore />} />
                            <Route path="notifications" element={<SellerNotifications />} />
                            <Route path="settings" element={<SellerSettings />} />
                            <Route path="admin-control" element={<AdminSellerControlPage />} />
                        </Route>

                        <Route path="vendor/products" element={<Navigate to="/seller/products" replace />} />
                        <Route path="vendor/dashboard" element={<Navigate to="/seller/dashboard" replace />} />
                        <Route path="vendor/publish" element={<Navigate to="/seller/products/new" replace />} />
                        <Route path="vendor/new-post" element={<Navigate to="/seller/products/new" replace />} />
                        <Route path="vendor/apply" element={<VendorApplicationPage />} />
                        <Route path="vendor/application" element={<VendorApplicationPage />} />
                        <Route path="agent-portal/:token" element={<AgentPortalPage />} />
                        <Route path="support-rooms" element={<SupportRoomsPage />} />
                        <Route path="live-rooms" element={<LiveRoomsDiscoveryPage />} />
                        <Route path="live" element={<LivePortalPage onAddToCart={addToCart} onBuyNow={buyNow} cartCount={cartCount} />} />
                        <Route path="live/:roomId" element={<LiveRoomPage onAddToCart={addToCart} onBuyNow={buyNow} />} />
                        <Route path="live-room/:roomId" element={<LiveRoomPage onAddToCart={addToCart} onBuyNow={buyNow} />} />
                        <Route path="meeting/:roomId" element={<MeetingJoinPage />} />
                        <Route path="download" element={<DownloadPage />} />
                        <Route path="vip" element={<VipPage />} />
                        <Route path="live-portal" element={<LivePortalPage onAddToCart={addToCart} onBuyNow={buyNow} cartCount={cartCount} />} />
                        <Route path="magazine" element={<MagazinePage />} />
                        {/* Stores, Sellers, Following & Live */}
                        <Route path="stores" element={<StoresPage />} />
                        <Route path="following" element={<FollowingFeedPage />} />
                        <Route path="@:username" element={<SellerPublicProfilePage />} />
                        <Route path="seller/:sellerSlug" element={<SellerPublicProfilePage />} />
                        <Route path="store/:slug" element={<SellerPublicProfilePage />} />
                        <Route path="status" element={<StatusPage />} />
                        <Route path=":slug" element={<DynamicPage storeSettings={storefrontSettings} />} />
                    </Route>

                    {/* 3. Backward Compatible Legacy Routes -> Safe Market Redirect */}
                    <Route path="/home" element={<LegacyRouteRedirect />} />
                    <Route path="/products" element={<LegacyRouteRedirect />} />
                    <Route path="/product/:id" element={<LegacyRouteRedirect />} />
                    <Route path="/products/:id" element={<LegacyRouteRedirect />} />
                    <Route path="/category/:name" element={<LegacyRouteRedirect />} />
                    <Route path="/cart" element={<LegacyRouteRedirect />} />
                    <Route path="/checkout" element={<LegacyRouteRedirect />} />
                    <Route path="/orders" element={<LegacyRouteRedirect />} />
                    <Route path="/contact" element={<LegacyRouteRedirect />} />
                    <Route path="/login" element={<LegacyRouteRedirect />} />
                    <Route path="/register" element={<LegacyRouteRedirect />} />
                    <Route path="/forgot-password" element={<LegacyRouteRedirect />} />
                    <Route path="/complete-profile" element={<LegacyRouteRedirect />} />
                    <Route path="/account/*" element={<LegacyRouteRedirect />} />
                    <Route path="/videos" element={<LegacyRouteRedirect />} />
                    <Route path="/video/:id" element={<LegacyRouteRedirect />} />
                    <Route path="/vendor/*" element={<LegacyRouteRedirect />} />
                    <Route path="/support-rooms" element={<LegacyRouteRedirect />} />
                    <Route path="/live-rooms" element={<LegacyRouteRedirect />} />
                    <Route path="/live" element={<LegacyRouteRedirect />} />
                    <Route path="/live/:roomId" element={<LegacyRouteRedirect />} />
                    <Route path="/live-room/:roomId" element={<LegacyRouteRedirect />} />
                    <Route path="/meeting/:roomId" element={<LegacyRouteRedirect />} />
                    <Route path="/download" element={<LegacyRouteRedirect />} />
                    <Route path="/vip" element={<LegacyRouteRedirect />} />
                    <Route path="/live-portal" element={<LegacyRouteRedirect />} />
                    <Route path="/magazine" element={<LegacyRouteRedirect />} />
                    <Route path="/sourcing" element={<LegacyRouteRedirect />} />
                    <Route path="/shipping" element={<LegacyRouteRedirect />} />
                    <Route path="/status" element={<LegacyRouteRedirect />} />
                    <Route path="/stores" element={<LegacyRouteRedirect />} />
                    <Route path="/following" element={<LegacyRouteRedirect />} />

                    {/* Country-only route like /us or /sa -> redirect to /us/en/ or /sa/ar/ */}
                    <Route path="/:country" element={<LegacyRouteRedirect />} />
                </Routes>
            </main>

            {(!isRootSelector && !isAuthPage && !isAgentPortal && !isVideosPage && !isLiveRoom && !isMeeting && !isSeller) && (
                <footer className="glass-footer luxvono-global-footer">
                    {/* Top Trust Pillars Strip */}
                    <div className="footer-trust-pillars-container">
                        <div className="container footer-trust-pillars-grid">
                            <div className="footer-pillar-card">
                                <div className="footer-pillar-icon">
                                    <Truck size={22} color="#111827" />
                                </div>
                                <div className="footer-pillar-text">
                                    <h4>{t('PRIORITY_GLOBAL_DELIVERY')}</h4>
                                    <p>{t('PRIORITY_GLOBAL_DELIVERY_DESC')}</p>
                                </div>
                            </div>
                            <div className="footer-pillar-card">
                                <div className="footer-pillar-icon">
                                    <ShieldCheck size={22} color="#111827" />
                                </div>
                                <div className="footer-pillar-text">
                                    <h4>{t('SECURE_CHECKOUT_TITLE')}</h4>
                                    <p>{t('SECURE_CHECKOUT_DESC')}</p>
                                </div>
                            </div>
                            <div className="footer-pillar-card">
                                <div className="footer-pillar-icon">
                                    <Gem size={22} color="#111827" />
                                </div>
                                <div className="footer-pillar-text">
                                    <h4>{t('CURATED_AUTHENTICITY')}</h4>
                                    <p>{t('CURATED_AUTHENTICITY_DESC')}</p>
                                </div>
                            </div>
                            <div className="footer-pillar-card">
                                <div className="footer-pillar-icon">
                                    <Headphones size={22} color="#111827" />
                                </div>
                                <div className="footer-pillar-text">
                                    <h4>{t('DEDICATED_CONCIERGE')}</h4>
                                    <p>{t('DEDICATED_CONCIERGE_DESC')}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Main Columns Grid */}
                    <div className="container footer-main-content">
                        <div className="footer-grid-layout">
                            {/* Column 1: Brand & Contact */}
                            <div className="footer-col footer-col-brand">
                                <div className="logo premium-gradient footer-logo" style={{ fontSize: '24px', fontWeight: '900', letterSpacing: '1px' }}>
                                    {storefrontSettings.logoText || 'LUXVONO'}
                                </div>
                                <p className="footer-brand-desc">
                                    {t('PREMIER_GLOBAL_PLATFORM') || 'The Sovereign Luxury Marketplace • LUXVONO LLC'}
                                </p>
                                <div className="footer-contact-items">
                                    {(storefrontSettings?.socialMedia?.phone || '+1 (646) 372 7645') && (
                                        <a href={`tel:${storefrontSettings?.socialMedia?.phone || '+16463727645'}`} className="footer-contact-card">
                                            <div className="footer-contact-card-icon">
                                                <Phone size={16} />
                                            </div>
                                            <div className="footer-contact-card-info">
                                                <span className="footer-contact-card-label">{t('DIRECT_VIP_PHONE')}</span>
                                                <span className="footer-contact-card-val" dir="ltr">{storefrontSettings?.socialMedia?.phone || '+1 (646) 372 7645'}</span>
                                            </div>
                                        </a>
                                    )}
                                    <a href={`mailto:${storefrontSettings?.socialMedia?.email || 'support@luxvono.com'}`} className="footer-contact-card">
                                        <div className="footer-contact-card-icon">
                                            <Mail size={16} />
                                        </div>
                                        <div className="footer-contact-card-info">
                                            <span className="footer-contact-card-label">{t('OFFICIAL_SUPPORT_EMAIL')}</span>
                                            <span className="footer-contact-card-val" dir="ltr">{storefrontSettings?.socialMedia?.email || 'support@luxvono.com'}</span>
                                        </div>
                                    </a>
                                </div>
                            </div>

                            {/* Column 2: Customer Care & Dynamic Pages */}
                            <div className="footer-col">
                                <h3 className="footer-col-title">{t('CUSTOMER_CARE')}</h3>
                                <ul className="footer-links-list">
                                    <li><MarketLink to="/orders">{t('TRACK_ORDER')}</MarketLink></li>
                                    {footerPages.map(page => {
                                        const pageTitle = page.title;
                                        return (
                                            <li key={page.id || page.slug}>
                                                <MarketLink to={`/${page.slug}`}>{pageTitle}</MarketLink>
                                            </li>
                                        );
                                    })}
                                    <li><MarketLink to="/vip">{t('VIP_CLUB')}</MarketLink></li>
                                    <li><MarketLink to="/contact">{t('HELP_SUPPORT')}</MarketLink></li>
                                </ul>
                            </div>

                            {/* Column 3: About LUXVONO */}
                            <div className="footer-col">
                                <h3 className="footer-col-title">{t('ABOUT_LUXVONO')}</h3>
                                <ul className="footer-links-list">
                                    <li><MarketLink to="/about">{t('ABOUT_US_VISION')}</MarketLink></li>
                                    <li><MarketLink to="/vendor/products">{t('SUPPLIER_PORTAL')}</MarketLink></li>
                                    <li><MarketLink to="/magazine">{t('LUXVONO_MAGAZINE')}</MarketLink></li>
                                    <li><MarketLink to="/terms">{t('TERMS_CONDITIONS')}</MarketLink></li>
                                    <li><MarketLink to="/privacy">{t('PRIVACY_POLICY')}</MarketLink></li>
                                </ul>
                            </div>

                            {/* Column 4: Payment & Security */}
                            <div className="footer-col">
                                <h3 className="footer-col-title">{t('SECURE_PAYMENTS')}</h3>
                                <p className="footer-security-note">
                                    {t('SECURE_CHECKOUT_DESC')}
                                </p>
                                <div className="footer-payment-badges">
                                    <span className="payment-badge">VISA</span>
                                    <span className="payment-badge">MasterCard</span>
                                    <span className="payment-badge">Apple Pay</span>
                                    <span className="payment-badge">Mada</span>
                                    <span className="payment-badge">AMEX</span>
                                </div>
                                <div style={{ marginTop: '16px' }}>
                                    <MarketLink to="/contact" className="footer-concierge-btn">
                                        <span>{t('CONTACT_CONCIERGE_BTN')}</span>
                                    </MarketLink>
                                </div>
                            </div>
                        </div>

                        {/* Social Media & Community Channels Bar */}
                        {socialLinks.length > 0 && (
                            <div className="footer-social-bar" style={{
                                padding: '24px 0',
                                borderTop: '1px solid rgba(255, 255, 255, 0.08)',
                                borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                                marginTop: '32px',
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: '16px'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <span style={{ fontSize: '13px', fontWeight: '700', letterSpacing: '0.5px', color: '#FFFFFF' }}>
                                        {t('CONNECT_WITH_US')}
                                    </span>
                                </div>

                                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px' }}>
                                    {socialLinks.map(item => (
                                        <a
                                            key={item.key}
                                            href={item.href}
                                            target="_blank"
                                            rel="noreferrer"
                                            title={item.label}
                                            className="footer-social-btn"
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: '40px',
                                                height: '40px',
                                                borderRadius: '12px',
                                                background: 'rgba(255, 255, 255, 0.05)',
                                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                                color: item.color || '#fff',
                                                transition: 'all 0.3s ease',
                                                textDecoration: 'none'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.transform = 'translateY(-3px)';
                                                e.currentTarget.style.background = item.bgColor || 'rgba(255, 255, 255, 0.15)';
                                                e.currentTarget.style.borderColor = item.borderColor || '#FFFFFF';
                                                e.currentTarget.style.boxShadow = `0 6px 16px ${item.bgColor || 'rgba(0,0,0,0.3)'}`;
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.transform = 'translateY(0)';
                                                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                                                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                                                e.currentTarget.style.boxShadow = 'none';
                                            }}
                                        >
                                            {item.icon(19)}
                                        </a>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Bottom Copyright Strip */}
                        <div className="footer-bottom-bar">
                            <p className="footer-copy-text" style={{ direction: 'ltr' }}>
                                &copy; {new Date().getFullYear()} LUXVONO LLC. ALL RIGHTS RESERVED.
                            </p>
                            <div className="footer-bottom-tagline">
                                <span>{t('GLOBAL_STANDARDS') || 'The Sovereign Luxury Standard'}</span>
                            </div>
                        </div>
                    </div>
                </footer>
            )}

            {(!isRootSelector && !isAgentPortal && !isSeller) && (
                <>
                    <RealtimeInAppBanner />
                    {(!isLiveRoom && !isMeeting && !isProductPage) && <BottomNav cartCount={cartCount} storeSettings={storefrontSettings} />}
                    <SearchOverlay open={searchOpen} onClose={handleCloseSearch} products={allProducts} initialMode={initialSearchMode} initialImage={initialSearchImage} />
                    {(!isVideosPage && !isLiveRoom && !isMeeting && !isProductPage) && <ChatWidget />}
                </>
            )}
        </div >
    );
};

export default App;
